import React, { useMemo, useState } from "react"
import "./FaqPage.css"

// Each FAQ item: { id, question, answer } where `answer` is JSX.
const FAQ_SECTIONS = [
  {
    id: "about",
    title: "About EnergyPulseWorld",
    items: [
      {
        id: "about-1",
        q: "What is EnergyPulseWorld?",
        a: (
          <>
            <p>
              EnergyPulseWorld is an energy, economic and trade intelligence
              platform providing structured country indicators, market data
              and analytical reports covering the relationship between energy
              markets, industrial activity and international trade.
            </p>
            <p>
              The platform combines regularly updated datasets with premium
              weekly analytical reports covering global developments and
              regional markets in Asia, the Middle East and North Africa,
              and Sub-Saharan Africa.
            </p>
          </>
        ),
      },
      {
        id: "about-2",
        q: "Who is EnergyPulseWorld designed for?",
        a: (
          <>
            <p>
              EnergyPulseWorld is intended for organizations and professionals
              whose decisions are affected by energy markets, commodity costs,
              industrial production and international trade.
            </p>
            <p>
              Typical users include energy companies, manufacturers, fertilizer
              and materials producers, banks, investment professionals,
              consulting firms, logistics companies, importers and exporters,
              chambers of commerce, government institutions and policy organizations.
            </p>
          </>
        ),
      },
    ],
  },
  {
    id: "country-intelligence",
    title: "Country Intelligence",
    items: [
      {
        id: "ci-1",
        q: "What country-level information is available?",
        a: (
          <>
            <p>Country intelligence brings together complementary indicators covering:</p>
            <ul>
              <li>Macroeconomic conditions</li>
              <li>Electricity and energy production mix</li>
              <li>Energy and energy-sensitive commodity prices</li>
              <li>Commodity import trends</li>
              <li>Trade administration and market-access indicators</li>
              <li>Selected doing-business and trade insights</li>
            </ul>
          </>
        ),
      },
      {
        id: "ci-2",
        q: "Which countries and regions are covered?",
        a: (
          <>
            <p>
              EnergyPulseWorld currently concentrates on selected economies
              across Asia, the Middle East and North Africa, and Sub-Saharan Africa.
            </p>
            <p>
              Asian coverage includes major economies such as China, India and
              Japan together with ASEAN markets. Regional coverage is progressively
              expanded as reliable datasets and market intelligence become available.
            </p>
          </>
        ),
      },
      {
        id: "ci-3",
        q: "Can I compare countries using the platform?",
        a: (
          <p>
            Yes. A core objective of EnergyPulseWorld is to support country
            comparison using economic, energy, commodity and trade indicators,
            allowing users to assess markets from several perspectives rather
            than relying on a single measure.
          </p>
        ),
      },
    ],
  },
  {
    id: "macroeconomics",
    title: "Macroeconomic Indicators",
    items: [
      {
        id: "macro-1",
        q: "Which macroeconomic indicators are available?",
        a: (
          <>
            <p>The principal indicators include:</p>
            <ul>
              <li><strong>Gross Domestic Product (GDP)</strong> – economic growth and activity.</li>
              <li><strong>Consumer Price Index (CPI)</strong> – inflation experienced by consumers.</li>
              <li><strong>Producer Price Index (PPI)</strong> – price and cost pressures affecting producers and industry.</li>
              <li><strong>Foreign Direct Investment (FDI)</strong> – cross-border investment flows and market attractiveness.</li>
            </ul>
          </>
        ),
      },
      {
        id: "macro-2",
        q: "Why does EnergyPulseWorld include both CPI and PPI?",
        a: (
          <>
            <p>
              CPI and PPI provide different views of inflation. CPI primarily
              reflects changes in prices faced by consumers, while PPI gives
              greater insight into costs affecting producers.
            </p>
            <p>
              For energy-intensive industries, PPI can be especially useful because
              changes in fuel, electricity, transport and raw-material costs may affect
              producers before those pressures are fully reflected in consumer prices.
            </p>
          </>
        ),
      },
      {
        id: "macro-3",
        q: "How should the macroeconomic indicators be used?",
        a: (
          <p>
            The indicators are designed for comparative and trend analysis rather
            than to be interpreted in isolation. Users can, for example, examine
            whether rising energy costs coincide with producer inflation, slowing
            GDP growth or changes in foreign investment.
          </p>
        ),
      },
    ],
  },
  {
    id: "energy-mix",
    title: "Energy Mix",
    items: [
      {
        id: "mix-1",
        q: "What does the Energy Mix section show?",
        a: (
          <p>
            The Energy Mix section provides an overview of electricity generation
            and national energy structures. It helps users understand how heavily
            markets depend on fossil fuels and how renewable or other non-fossil
            sources are developing over time.
          </p>
        ),
      },
      {
        id: "mix-2",
        q: "Which energy sources are covered?",
        a: (
          <>
            <p>Core generation categories include:</p>
            <ul>
              <li>Coal</li>
              <li>Oil</li>
              <li>Natural gas</li>
              <li>Hydropower</li>
              <li>Wind</li>
              <li>Solar</li>
            </ul>
            <p>
              Where relevant, the accompanying analysis may also discuss important
              sources such as nuclear power, geothermal energy, biomass or electricity imports.
            </p>
          </>
        ),
      },
      {
        id: "mix-3",
        q: "Why is the energy mix important for businesses?",
        a: (
          <p>
            A country's generation structure can influence electricity prices,
            supply security, exposure to imported fuels and industrial competitiveness.
            A market dependent on imported gas, for example, may respond differently
            to a global gas-price shock than one relying heavily on domestic hydro,
            solar or other local sources.
          </p>
        ),
      },
    ],
  },
  {
    id: "commodity-prices",
    title: "Commodity Prices",
    items: [
      {
        id: "cp-1",
        q: "Which commodity prices does EnergyPulseWorld track?",
        a: (
          <>
            <p>
              EnergyPulseWorld monitors selected energy commodities and
              energy-sensitive industrial products, including:
            </p>
            <ul>
              <li>Oil and petroleum products</li>
              <li>Natural gas and related fuels</li>
              <li>Urea</li>
              <li>Ammonia</li>
              <li>Cement</li>
              <li>Steel</li>
            </ul>
          </>
        ),
      },
      {
        id: "cp-2",
        q: "How frequently are commodity prices updated?",
        a: (
          <p>
            The principal commodity datasets are monitored on a monthly basis,
            allowing users to assess month-over-month price developments and
            identify whether prices are increasing, decreasing or remaining stable.
          </p>
        ),
      },
      {
        id: "cp-3",
        q: "Why are fertilizer, cement and steel included on an energy platform?",
        a: (
          <>
            <p>
              These industries are highly energy-sensitive. Ammonia and urea
              production depend heavily on natural gas and other energy inputs,
              while steel and cement production require significant thermal energy,
              electricity and transport.
            </p>
            <p>
              Changes in energy prices can therefore affect production costs,
              competitiveness, import requirements and market prices.
            </p>
          </>
        ),
      },
      {
        id: "cp-4",
        q: "Does EnergyPulseWorld provide analysis in addition to price data?",
        a: (
          <p>
            Yes. Where appropriate, country and commodity records include concise
            analytical summaries explaining recent price direction, month-over-month
            movements and relevant economic or market factors.
          </p>
        ),
      },
    ],
  },
  {
    id: "trade",
    title: "Trade & Doing Business",
    items: [
      {
        id: "trade-1",
        q: "What does the Trade & Doing Business section cover?",
        a: (
          <>
            <p>
              This section provides selected indicators concerning the administrative
              environment businesses may face when importing goods or operating across borders.
            </p>
            <ul>
              <li>Import licensing requirements</li>
              <li>Indicative time required to obtain import licences</li>
              <li>Customs and clearance processes</li>
              <li>Trade administration procedures</li>
              <li>Customs digitalization and Single Window status</li>
              <li>Relevant preferential trade arrangements</li>
              <li>Tariff and regulatory considerations</li>
              <li>Trade Administration Index indicators</li>
            </ul>
          </>
        ),
      },
      {
        id: "trade-2",
        q: "What is the Trade Administration Index?",
        a: (
          <p>
            The Trade Administration Index provides a comparative perspective on
            the procedures and administrative environment surrounding international trade.
            EnergyPulseWorld combines relevant indicators with country analysis to help users
            understand whether licensing, customs and border procedures may facilitate or
            constrain commercial activity.
          </p>
        ),
      },
      {
        id: "trade-3",
        q: "Can I find out how long it takes to obtain an import licence?",
        a: (
          <p>
            Where reliable country information is available, EnergyPulseWorld provides
            indicative information on licensing procedures and expected processing times.
            Actual timeframes may vary depending on the product, competent authority,
            importer status and applicable regulations.
          </p>
        ),
      },
      {
        id: "trade-4",
        q: "Does EnergyPulseWorld analyze commodity import exposure?",
        a: (
          <p>
            Yes. Selected country datasets track imports of important industrial and
            energy-sensitive products to help users understand market dependency,
            demand trends and exposure to international prices, freight costs and
            supply-chain disruptions.
          </p>
        ),
      },
    ],
  },
  {
    id: "weekly-insights",
    title: "Premium Weekly Insights",
    items: [
      {
        id: "wi-1",
        q: "What are EnergyPulseWorld Weekly Insights?",
        a: (
          <>
            <p>
              Weekly Insights are premium analytical reports examining consequential
              developments in energy markets and their impact on economies, industry
              and international trade.
            </p>
            <p>
              Rather than reporting energy prices alone, the reports examine how
              energy-market developments can transmit into industrial costs, fertilizers,
              metals, manufacturing, transportation, trade flows and broader economic conditions.
            </p>
          </>
        ),
      },
      {
        id: "wi-2",
        q: "Which regions are covered by the weekly reports?",
        a: (
          <>
            <p>Weekly reporting includes:</p>
            <ul>
              <li>Global energy and industry outlook</li>
              <li>Asia</li>
              <li>Middle East and North Africa</li>
              <li>Sub-Saharan Africa</li>
            </ul>
          </>
        ),
      },
      {
        id: "wi-3",
        q: "What does a typical weekly report contain?",
        a: (
          <ul>
            <li>Executive summary</li>
            <li>Major energy-market developments</li>
            <li>Oil and natural-gas trends</li>
            <li>Supply and geopolitical developments</li>
            <li>Regional economic implications</li>
            <li>Impact on fertilizers and ammonia</li>
            <li>Impact on steel, aluminium and cement</li>
            <li>Trade and logistics implications</li>
            <li>Week-over-week comparison</li>
            <li>Key risks and opportunities</li>
            <li>Forward-looking outlook</li>
            <li>Professional reference sources</li>
          </ul>
        ),
      },
      {
        id: "wi-4",
        q: "How are the weekly reports different from ordinary news coverage?",
        a: (
          <>
            <p>
              EnergyPulseWorld connects developments across markets. Instead of looking
              at an oil-price movement in isolation, the analysis may examine its effect
              on gas, fertilizers, manufacturing costs, inflation, trade balances and
              particular regions or industries.
            </p>
            <p>
              The emphasis is on business implications and interconnected market effects,
              rather than simply summarizing news.
            </p>
          </>
        ),
      },
    ],
  },
  {
    id: "methodology",
    title: "Data & Methodology",
    items: [
      {
        id: "method-1",
        q: "Where does EnergyPulseWorld obtain its information?",
        a: (
          <>
            <p>
              EnergyPulseWorld draws on reputable international organizations,
              government and national statistical institutions, energy agencies,
              commodity-market sources, trade databases and established financial
              and news organizations.
            </p>
            <p>
              Depending on the dataset, sources may include the World Bank, IMF,
              International Energy Agency, WTO, UN agencies, national statistical offices,
              energy ministries, customs authorities and other recognized sources.
            </p>
          </>
        ),
      },
      {
        id: "method-2",
        q: "Are all figures official data?",
        a: (
          <p>
            EnergyPulseWorld gives preference to official and independently reported
            data wherever available. Where official data are published with significant
            delays, provisional, derived, benchmark or estimated figures may be used
            to provide more recent market visibility and are identified accordingly.
          </p>
        ),
      },
      {
        id: "method-3",
        q: "How often is the data updated?",
        a: (
          <p>
            Update frequency depends on the indicator. Commodity-price information is
            generally monitored monthly, while major macroeconomic, energy and trade
            datasets are refreshed as new authoritative information becomes available.
            Premium analytical reports are published weekly.
          </p>
        ),
      },
    ],
  },
  {
    id: "subscriptions",
    title: "Subscriptions",
    items: [
      {
        id: "sub-1",
        q: "What subscription plans are available?",
        a: (
          <p>
            EnergyPulseWorld offers subscription levels designed for different users,
            including <strong>Pro</strong> and <strong>Enterprise</strong> plans.
          </p>
        ),
      },
      {
        id: "sub-2",
        q: "What is included in the Pro subscription?",
        a: (
          <>
            <p>
              The <strong>Pro subscription</strong> is designed for professionals who
              require continuous access to current EnergyPulseWorld intelligence,
              including premium country intelligence, market indicators and current
              weekly analytical reports, subject to the applicable subscription package.
            </p>
            <div className="epw-plan-card">
              <strong>Best suited for:</strong> individual professionals and regular users
              monitoring energy, industry, economic and trade developments.
            </div>
          </>
        ),
      },
      {
        id: "sub-3",
        q: "What is included in the Enterprise subscription?",
        a: (
          <>
            <p>
              The <strong>Enterprise subscription</strong> is designed for organizations
              requiring broader access and enhanced analytical functionality.
            </p>
            <ul>
              <li>Premium platform content and current weekly analysis</li>
              <li>Access to archived EnergyPulseWorld reports</li>
              <li>Download and export functionality</li>
              <li>Exportable charts</li>
              <li>Downloadable infographics</li>
              <li>Enhanced use of EnergyPulseWorld content in internal analysis and presentations</li>
            </ul>
            <div className="epw-plan-card">
              <strong>Best suited for:</strong> corporations, consulting firms, financial
              institutions, trade organizations and professional teams.
            </div>
          </>
        ),
      },
      {
        id: "sub-4",
        q: "Can Enterprise subscribers access previous weekly reports?",
        a: (
          <p>
            Yes. Historical report access is an enhanced Enterprise capability,
            allowing organizations to review earlier market developments and compare
            current conditions with previous periods.
          </p>
        ),
      },
      {
        id: "sub-5",
        q: "Can I download charts and infographics?",
        a: (
          <p>
            Download and export capabilities for EnergyPulseWorld charts and
            infographics are available through the Enterprise subscription,
            subject to applicable subscription and licensing terms.
          </p>
        ),
      },
      {
        id: "sub-6",
        q: "Which subscription should I choose?",
        a: (
          <>
            <p>
              <strong>Pro</strong> is appropriate for professionals primarily seeking
              access to current premium intelligence, country indicators and weekly reports.
            </p>
            <p>
              <strong>Enterprise</strong> is better suited to organizations and teams
              requiring historical report access together with download, export and
              reusable visualization capabilities.
            </p>
          </>
        ),
      },
    ],
  },
  {
    id: "using-platform",
    title: "Using EnergyPulseWorld",
    items: [
      {
        id: "use-1",
        q: "Can EnergyPulseWorld support market-entry analysis?",
        a: (
          <p>
            Yes. Macroeconomic indicators, energy structures, commodity-price trends,
            import dependency and trade-administration information can collectively
            provide a useful starting point for assessing new markets and identifying
            areas requiring deeper commercial or regulatory investigation.
          </p>
        ),
      },
      {
        id: "use-2",
        q: "Can EnergyPulseWorld help identify investment opportunities?",
        a: (
          <p>
            EnergyPulseWorld can support investment and market screening through
            consolidated economic, energy, industrial and trade intelligence.
            It does not provide individualized investment recommendations and should
            not replace appropriate financial, legal or commercial due diligence.
          </p>
        ),
      },
      {
        id: "use-3",
        q: "Is EnergyPulseWorld a substitute for legal, customs or investment advice?",
        a: (
          <p>
            No. EnergyPulseWorld provides analytical and informational intelligence
            intended to support market assessment and decision-making. Users should
            obtain appropriate professional advice before making legal, regulatory,
            customs, investment or contractual decisions.
          </p>
        ),
      },
      {
        id: "use-4",
        q: "Can EnergyPulseWorld data be used in corporate presentations and reports?",
        a: (
          <p>
            Usage depends on the applicable subscription and licensing terms.
            Enterprise functionality is designed to provide enhanced access to
            downloadable and exportable charts and infographics for organizational
            analytical workflows.
          </p>
        ),
      },
      {
        id: "use-5",
        q: "How can I contact EnergyPulseWorld?",
        a: (
          <p>
            Please use the <a href="/contact">contact form</a> on EnergyPulseWorld.com for subscription,
            corporate-access, partnership or general enquiries.
          </p>
        ),
      },
    ],
  },
]

// Flatten for search, keeping a text blob per item for matching.
const flattenText = (node) => {
  if (node == null) return ""
  if (typeof node === "string" || typeof node === "number") return String(node)
  if (Array.isArray(node)) return node.map(flattenText).join(" ")
  if (node.props && node.props.children) return flattenText(node.props.children)
  return ""
}

const FaqPage = () => {
  const [query, setQuery] = useState("")
  const [openIds, setOpenIds] = useState(() => new Set())

  const normalizedQuery = query.trim().toLowerCase()

  const filteredSections = useMemo(() => {
    if (!normalizedQuery) return FAQ_SECTIONS

    return FAQ_SECTIONS.map((section) => ({
      ...section,
      items: section.items.filter((item) => {
        const blob = `${item.q} ${flattenText(item.a)}`.toLowerCase()
        return blob.includes(normalizedQuery)
      }),
    })).filter((section) => section.items.length > 0)
  }, [normalizedQuery])

  const visibleCount = filteredSections.reduce((sum, s) => sum + s.items.length, 0)

  const isOpen = (id) => {
    if (normalizedQuery) return true // auto-expand matches while searching
    return openIds.has(id)
  }

  const toggleItem = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const expandAll = () => {
    const all = new Set()
    filteredSections.forEach((s) => s.items.forEach((i) => all.add(i.id)))
    setOpenIds(all)
  }

  const collapseAll = () => {
    setOpenIds(new Set())
  }

  return (
    <div className="epw-faq-page">

      <main>
        <header className="epw-faq-hero">
          <div className="epw-faq-hero-inner">
            <p className="epw-eyebrow">EnergyPulseWorld</p>
            <h1>Frequently Asked Questions</h1>
            <p>
              Learn how EnergyPulseWorld combines macroeconomic, energy, commodity,
              trade and weekly market intelligence to help organizations understand
              how energy developments affect industry, investment and international trade.
            </p>
          </div>
        </header>

        <section className="epw-faq-main">
          <div className="epw-faq-wrap">

            <div className="epw-faq-toolbar">
              <div className="epw-faq-search">
                <span className="epw-search-icon" aria-hidden="true">⌕</span>
                <input
                  type="search"
                  placeholder="Search the FAQ..."
                  aria-label="Search frequently asked questions"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>

              <div className="epw-faq-actions" aria-label="FAQ controls">
                <button className="epw-btn" type="button" onClick={expandAll}>Expand all</button>
                <button className="epw-btn" type="button" onClick={collapseAll}>Collapse all</button>
              </div>
            </div>

            <div className="epw-faq-layout">
              <nav className="epw-faq-nav" aria-label="FAQ categories">
                <p className="epw-faq-nav-title">Categories</p>
                {FAQ_SECTIONS.map((section) => (
                  <a key={section.id} href={`#${section.id}`}>{section.title}</a>
                ))}
              </nav>

              <div className="epw-faq-content">
                {filteredSections.map((section) => (
                  <section className="epw-faq-section" id={section.id} key={section.id}>
                    <h2>{section.title}</h2>

                    {section.items.map((item) => (
                      <details
                        className="epw-faq-item"
                        key={item.id}
                        open={isOpen(item.id)}
                        onToggle={(e) => {
                          // Ignore toggle events fired while auto-open from search.
                          if (normalizedQuery) return
                          if (e.target.open !== openIds.has(item.id)) toggleItem(item.id)
                        }}
                      >
                        <summary onClick={(e) => {
                          if (normalizedQuery) e.preventDefault()
                        }}>
                          {item.q}
                        </summary>
                        <div className="epw-faq-answer">{item.a}</div>
                      </details>
                    ))}
                  </section>
                ))}

                {visibleCount === 0 && (
                  <div className="epw-no-results" style={{ display: "block" }}>
                    No matching questions were found. Try a different keyword.
                  </div>
                )}

                <section className="epw-faq-cta" aria-label="Subscription call to action">
                  <div>
                    <h2>Need deeper access to EnergyPulseWorld intelligence?</h2>
                    <p>
                      Explore Pro and Enterprise access for premium weekly insights,
                      country intelligence and enhanced analytical capabilities.
                    </p>
                  </div>

                  <a className="epw-cta-button" href="/login">Explore subscriptions</a>
                </section>

                <p className="epw-footnote">
                  EnergyPulseWorld provides informational and analytical content only.
                  Data availability, coverage and update frequency may vary by country,
                  indicator and source.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  )
}

export default FaqPage
